import { createTranslationRef } from '@backstage/core-plugin-api/alpha';

export const entityRegistrationFlowTranslationRef = createTranslationRef({
  id: 'entity-registration-flow',
  messages: {
    button: {
      select: 'Select',
      back: 'Back',
      next: 'Next',
      submit: 'Submit',
      analyze: 'Analyze',
      import: 'Import',
      refresh: 'Refresh',
    },
    errors: {
      yamlTitle: 'YAML title must end with ".yaml".',
      existingGithubUrl: 'Please provide a valid GitHub repository URL.',
      githubOrgUrl: 'Please provide a valid GitHub organization URL.',
      repoUrl: 'Please provide a valid entity URL.',
      noEntities: 'There are no entities at this location',
    },
    common: {
      publishEntity: 'Publish',
      manualPublishingTitle: 'Manual Publishing',
    },
    selectProvider: {
      selectOption: 'Select the option that applies to you',
      githubTitle: 'GitHub Enterprise Cloud',
      githubDesc:
        'Choose this option if you use GHEC (github.com or cariad.ghe.com) & want automated publishing',
      manualPublishingTitle: 'Manual Publishing',
      manualPublishingDesc:
        'Choose this option if you want to publish manually or when you use Bitbucket.',
    },
    githubProviderRepo: {
      yamlTitleLabel: 'YAML Title',
      yamlTitleTooltip:
        'Provide the name of the YAML file that contains your content definition. The file name is typically named {{yamlFilename}}',
      repoOptionText: 'Please select the option that most suits you',
      existingRepoText: 'Publish on Existing Repository',
      existingRepoDesc:
        'Connect and publish to an existing repository. Your content won’t be visible in XELERATE until the pull request is merged',
      repoUrlLabel: 'Repository URL',
      repoUrlPlaceholder: 'Enter the GitHub url for your repository',
      newRepoText: 'Publish on New Repository',
      newRepoDesc:
        'Create and publish to a new GitHub repository. The content will be published directly to the main branch and immediately appear in XELERATE',
      organizationUrlLabel: 'Organization URL',
      organizationUrlPlaceholder: 'e.g. https://github.com/org',
      repoNameLabel: 'Repository Name',
      repoNamePlaceholder: 'Select the name for your new repository',
    },
    createPrDialog: {
      desc: 'Your changes will be published via a pull request. Fill in the pull request details below to continue.',
      baseBranchLabel: 'Base Branch',
      baseBranchPlaceholder:
        'Specify the branch you want to merge into (e.g. main)',
      branchLabel: 'Branch Name',
      branchPlaceholder: 'Specify the branch name you want to create',
      pullrequestTitleLabel: 'Pull Request Title',
      pullrequestTitlePlaceholder: 'Write a clear title for this pull request',
      descriptionLabel: 'Pull Request Description',
      descriptionPlaceholder: 'Describe what this pull request adds or changes',
    },
    pullrequestSuccessDialog: {
      title: 'File Submitted via Pull Request',
      description: `Your file has been added to a pull request in your repository.
                It will appear in the catalog once the pull request is merged`,
      actions: {
        logs: 'View Logs',
        view_pr: 'View Pull Request',
      },
    },
    errorMessages: {
      githubAccess: {
        heading: 'No Writing Permission',
        newRepoDescription: ` You don’t have permission to create a repository under the selected
              organization. Please continue with manual register, or request the
              necessary access.`,
        createPrDescription: `You don’t have permission to create a pull request in the selected repository. Please continue with manual registration, or request the necessary access to proceed.`,
        action: {
          cancel: 'Cancel',
          back: 'Back',
        },
      },
      workflowFailed: {
        heading: 'Something Went Wrong',
        description:
          'Registration could not be completed successfully. Check the logs for detailed error information and try again later.',
      },
    },
    githubAuth: {
      title: 'Connect Your Repository',
      description:
        'We need permission to publish your content directly to your GitHub repository.',
      docsLink: {
        label: 'Learn more',
        url: '/docs/default/component/xelerate/catalog-generator/#step-3-authenticate-github-account',
      },
      oAuthApp: {
        title: 'Grant Access to Your Repository',
        description:
          'To publish your content, XELERATE needs a one-time GitHub read & write permission.',

        actions: {
          back: 'Back',
          grantAccess: 'Grant Access',
        },
      },
      githubApp: {
        title: 'Authorize XELERATE GitHub App',
        subTitle: `To sync data from your repository, the XELERATE GitHub App needs to
            be installed.`,
        description: {
          primary: 'Only a GitHub organization owner can do this.',
          secondary: `If you’re not an owner, you can skip this step for now and complete
            it later.`,
        },
        linkLabel: 'Install GitHub App',
        ghecDomainCheck: 'cariad.ghe.com',
        link: 'https://github.com/apps/cariad-xelerate',
        gheLink: 'https://cariad.ghe.com/apps/xelerate/cariad-xelerate',
        refreshDescription: `When you have successfully authorized GitHub. Please refresh the page to continue with the setup.`,
        noAuthorize: 'No Authorization Granted',
        refreshToContinue: 'Refresh to Continue',
        warning: {
          title: 'GitHub Installation Required',
          showdetails: 'Show details',
          description: `It looks like you haven't installed GitHub App yet. Without completing
                       this step, you won't be able to access the content you’re about to
                       register on XELERATE.`,
        },
        successTitle: 'Authorization Granted',
        successDescription:
          'You have been granted access to GitHub. Please push next in order to continue with the publishing process.',

        action: {
          refresh: 'Refresh',
          skip: 'Skip',
          skipLabel: 'Skip Authorization Step',
          authorize: 'Check Authorization',
        },
      },
    },
    createNewRepo: {
      heading: 'Registration successful',
      description:
        'Congratulations! Everything was published on your repo and registered on XELERATE. You can check them now',
      githubAppNotInstalled: {
        description:
          'Congratulations! Your content was published on your repository',
        action: 'View in Repository',
      },
    },
    reviewInfo: {
      fileInfoLabel: 'This file will be published to your repository',
      entityInfoLabel:
        'By adding this file the following content will be added to your XELERATE catalog',
    },
    manualPublishingSteps: {
      title: 'How to publish on XELERATE',
      downloadYaml: 'Download your YAML',
      learnMore: 'Learn more',
      step1Title: 'Download the generated YAML',
      step1Desc: `You may assign any filename you prefer,  but conventionally it's called `,
      step2Title: 'Store the YAML in your repository',
      step2Desc: `Place the file in a repository you use (GitHub is recommended)`,
      step3Title: 'Grant XELERATE read-only access',
      step3DescIntro:
        'XELERATE needs to be able to access the file in your repository:',
      step3DescLine1Title: 'Azure DevOps - EU region: ',
      step3DescLine1Desc:
        'Invite the technical user xelerate@cariad.technology to your projects. ',
      step3DescLine1Link:
        '/docs/default/component/xelerate/entity-publishing/#azure-devops-eu-tenant',
      step3DescLine2Title: 'Bitbucket: ',
      step3DescLine2Desc:
        'Add the system user “Xelerate” to your repository and grant them the permissions to view project-level information and read-only access. ',
      step3DescLine2Link:
        '/docs/default/component/xelerate/entity-publishing/#bitbucket-granting-the-xelerate-system-user-access',
      step3DescLine3Title: 'GitHub Enterprise Cloud (github.com): ',
      step3DescLine3Desc: 'Install the XELERATE GitHub App. ',
      step3DescLine3Link:
        '/docs/default/component/xelerate/entity-publishing/#github-granting-the-xelerate-app-access',
      step3DescLine4Title: 'GitHub Enterprise Server (git.hub.vwgroup.com): ',
      step3DescLine4Desc: 'Install the XELERATE GitHub Enterprise App.',
      step3DescLine4Link:
        '/docs/default/component/xelerate/entity-publishing/#github-granting-the-xelerate-app-access',
      step3DescLine5Title: 'GitHub Enterprise Cloud EU (cariad.ghe.com): ',
      step3DescLine5Desc: 'Install the XELERATE GitHub Enterprise App.',
      step3DescLine5Link:
        '/docs/default/component/xelerate/entity-publishing/#github-granting-the-xelerate-app-access',
      step4Title: `Import Your Project's Entity File`,
      step4Desc: `Once you have created and saved your entity file and enabled access, you can import your project's catalog file into XELERATE`,
      step4DescAddition: ` by clicking "NEXT"`,
      noShowPublishingStepText: 'Don’t show me this next time',
    },
    changeLocation: {
      guideText: 'How to move entities manually',
      manualFlowTitle: 'Change Repository Location',
      newRepoLocation: 'New Repo Location',
      chooseNewlocation: 'Choose New Location',
      catalogNew: 'The following entities will be moved to this repository',
      locationChangeSubmitted: 'Location Change Submitted',
      locationChangeDesc:
        'Your entity has been submitted to the repository through a pull request.',
      locationChangeWarning:
        'The entity will be unavailable in the catalog until the pull request is merged. Once merged, refresh the catalog. It may take a few minutes to reappear.',
      locationChangeSuccess: 'Location changed successfully',
      locationChangeSuccessDesc:
        'Congratulations! Your entities have been moved to your new repo. You can view them now.',
      ghRepoOptionText: 'Please select the option that best suits you',
      ghExistingRepoText: 'Move to an Existing Repository',
      ghExistingRepoDesc:
        'Connect an existing repository and move your entities there. They will appear in XELERATE once the pull request is merged.',
      ghRepoUrlPlaceholder: 'Enter the new repository URL',
      ghNewRepoText: 'Move to a New Repository',
      ghNewRepoDesc:
        'Create a new GitHub repository and move your entities there. They will appear in XELERATE once the move is complete.',
      createPRDialogDesc:
        'Your entities will be moved via a pull request. Enter the details below to continue.',
      reviewInfoDailogFileInfoLabel:
        'This file will be moved to your repository',
      reviewInfoDailogEntityInfoLabel:
        'The following entity will be moved to your repository',
      createNewRepodesc:
        'Congratulations! Your entities have been moved to your new repo. You can view them now.',
    },
    changeLocationsManuallySteps: {
      title: 'How to move entities manually',
      learnMore: 'Learn more',
      step1Title: 'Download the entity YAML',
      step1Desc: `Download the generated YAML file from XELERATE. You can use any filename, but catalog-info.yaml is the conventional name.`,
      step2Title: 'Add the YAML to your repository',
      step2Desc: `Copy the downloaded file to the repository where you want the entity to be managed.`,
      step4Title: `Register the entity in XELERATE`,
      step4Desc: `Return to XELERATE and provide the URL of the YAML file in your new repository to register the entity.`,
      step4DescAddition: ` by clicking "NEXT"`,
      noShowPublishingStepText: 'Don’t show me this next time',
    },
    manualPublishing: {
      repoLocation: 'Repository Location',
      review: 'Review',
      repoUrlLabel: 'Repository URL',
      repoUrlPlaceholder: 'Enter a valid url starting with http:// or https://',
      repoUrlTooltip: 'Enter the full path to your entity file',
      manualPublish: 'How to publish on XELERATE',
      catalogNew: 'The following content will be added to the catalog',
      catalogExist: 'The following location already exist in the catalog',
      fixNow: 'Fix Now',
    },
    manualPublishingSuccess: {
      title: 'Registration successful',
      description:
        'Congratulations! Everything was registered on XELERATE. You can now check them',
      viewInRepo: 'View in Repository',
    },
    logViewer: {
      title: 'Log Activity',
    },
  },
});
